import { useNavigate } from "react-router-dom";
import { goBackOr } from "@/lib/goBackOr";
import Navbar from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { ShieldCheck, FileText, Lock, Building2, ArrowLeft } from "lucide-react";

export const CHARTER_VERSION = "1.1";

// Exposée pour pouvoir l'afficher intégralement depuis Register.jsx et une page publique /charte
export const CHARTER_SECTIONS = [
  {
    title: "1. Objet de la plateforme",
    body: (
      <>
        <p>
          RdvMarket est une marketplace exploitée par <strong>KLIKANGO SAS</strong> (SIREN 948 465 331, RCS Créteil,
          capital 2 008 €, siège 16 Avenue Sainte-Marie, 94160 Saint-Mandé, TVA FR42 948 465 331), ci-après
          « la plateforme ».
        </p>
        <p>
          La plateforme met en relation des professionnels souhaitant vendre et acheter des leads / dossiers qualifiés.
          Les prospects/clients concernés peuvent être des professionnels ou des particuliers selon les annonces.
          KLIKANGO n'est ni le producteur, ni le propriétaire, ni le responsable du contenu substantiel des leads
          échangés. Elle agit exclusivement en qualité d'intermédiaire technique au sens de l'article 6 de la loi LCEN
          n° 2004-575 du 21 juin 2004.
        </p>
        <p>
          La plateforme <strong>ne réalise pas de démarchage</strong> pour le compte des utilisateurs et ne décide pas des
          listes de contacts, des créneaux, des scripts, ni des finalités de prospection. L'utilisation des outils d'appel
          doit se faire dans le respect du droit applicable (notamment RGPD, ePrivacy et code de la consommation), et il
          est interdit d'utiliser la plateforme pour des appels non sollicités à destination de particuliers lorsqu'un
          consentement préalable est requis.
        </p>
      </>
    ),
  },
  {
    title: "2. Responsabilité RGPD du vendeur",
    body: (
      <>
        <p>
          En publiant un lead, le <strong>vendeur</strong> garantit et déclare sous sa seule responsabilité :
        </p>
        <ul className="list-disc ml-5 space-y-1">
          <li>avoir obtenu le consentement libre, éclairé et spécifique du prospect pour la cession de ses
            données à des tiers commerciaux, conformément aux articles 6 et 7 du RGPD (UE 2016/679) ;</li>
          <li>avoir informé le prospect du transfert de ses données à un tiers et du droit d'accès,
            rectification, effacement, limitation, portabilité et opposition ;</li>
          <li>respecter la loi Informatique et Libertés n° 78-17 modifiée, la directive ePrivacy, la loi LCEN
            ainsi que le code de la consommation (L.221-16 et s. sur le démarchage téléphonique et Bloctel) ;</li>
          <li>ne publier aucun lead issu d'une source frauduleuse, illégale, ou obtenue en violation des règles
            ci-dessus.</li>
        </ul>
        <p className="mt-2">
          Le vendeur reconnaît être responsable de traitement (ou responsable conjoint avec l'acheteur) des données
          transmises via la plateforme, et assume seul toute conséquence civile, pénale ou administrative liée à
          une éventuelle violation de la réglementation.
        </p>
        <p className="mt-2">
          En particulier, lorsque le lead concerne un <strong>particulier</strong>, le vendeur garantit que le contact a
          été collecté et transmis de manière licite et qu'il existe une base légale permettant le <strong>rappel</strong>
          (par exemple demande de rappel, relation contractuelle en cours, ou consentement préalable lorsque requis). En cas
          de contestation ou de contrôle, le vendeur s'engage à fournir aux autorités compétentes (ou à la plateforme sur
          demande légitime) les éléments permettant d'établir la conformité.
        </p>
      </>
    ),
  },
  {
    title: "3. Responsabilité RGPD de l'acheteur",
    body: (
      <>
        <p>
          L'<strong>acheteur</strong> s'engage, dès réception d'un lead :
        </p>
        <ul className="list-disc ml-5 space-y-1">
          <li>à traiter les données personnelles du prospect conformément au RGPD et à la loi Informatique et
            Libertés, en tant que responsable de traitement distinct ;</li>
          <li>à informer le prospect, lors du premier contact, de la source de ses données (« RdvMarket /
            vendeur anonymisé ») et des finalités poursuivies ;</li>
          <li>à respecter le dispositif Bloctel (loi n° 2014-344) pour toute sollicitation téléphonique ;</li>
          <li>à mettre en œuvre les mesures techniques et organisationnelles nécessaires à la sécurité des
            données (art. 32 RGPD).</li>
        </ul>
        <p className="mt-2">
          L'acheteur s'engage à <strong>cesser immédiatement</strong> toute sollicitation si le prospect s'y oppose, et à
          s'abstenir de toute relance ultérieure en cas d'opposition. Il est seul responsable de la conformité de ses
          appels et de ses pratiques commerciales.
        </p>
      </>
    ),
  },
  {
    title: "4. Exclusion de responsabilité de KLIKANGO",
    body: (
      <>
        <p>
          KLIKANGO n'accède ni ne contrôle le contenu substantiel des leads publiés avant leur cession.
          KLIKANGO <strong>ne garantit ni l'exactitude, ni la licéité, ni la conformité RGPD</strong> des leads
          fournis par les vendeurs.
        </p>
        <p>
          En conséquence, KLIKANGO ne pourra en aucun cas être tenue pour responsable :
        </p>
        <ul className="list-disc ml-5 space-y-1">
          <li>de toute violation du RGPD, de la loi Informatique et Libertés, de la loi Bloctel ou de toute
            autre réglementation applicable commise par un utilisateur ;</li>
          <li>de toute sanction de la CNIL, d'une autorité équivalente (BE, CH) ou d'une juridiction à l'encontre
            d'un utilisateur ;</li>
          <li>de toute action, plainte ou dommage subi par un prospect ou un tiers du fait de l'utilisation des
            données achetées ou vendues sur la plateforme ;</li>
          <li>du comportement, des agissements ou des manquements d'un autre utilisateur (vendeur ou acheteur).</li>
        </ul>
        <p className="mt-2">
          L'utilisateur s'engage à <strong>garantir et relever indemne</strong> KLIKANGO de toute réclamation,
          condamnation, amende administrative ou indemnité qui lui serait réclamée en lien avec son activité sur
          la plateforme.
        </p>
        <p className="mt-2">
          En cas de plainte, réquisition ou demande émanant d'une autorité compétente, KLIKANGO pourra coopérer et
          transmettre les informations strictement nécessaires (identifiants de compte, journaux techniques, et le cas
          échéant enregistrements) dans le respect du cadre légal.
        </p>
      </>
    ),
  },
  {
    title: "5. Anti-fraude, anti-détournement & enregistrement des appels",
    body: (
      <>
        <p>
          Tous les appels passés via la plateforme sont <strong>anonymisés</strong> (aucun numéro n'est visible)
          et <strong>enregistrés</strong> à des fins exclusives de lutte contre la fraude et de règlement des
          litiges (base légale : intérêt légitime, art. 6-1-f RGPD).
        </p>
        <p>
          L'acheteur et le prospect sont informés de l'enregistrement avant le début de l'appel (message vocal
          automatique). Les enregistrements sont chiffrés, conservés 12 mois maximum, et ne sont accessibles qu'à
          l'équipe admin de KLIKANGO en cas de litige ou à l'IA d'analyse anti-détournement (traitement
          automatisé purement interne).
        </p>
        <p>
          Tout contournement de la plateforme (prise de contact direct, paiement hors escrow, revente non
          autorisée) entraîne la suspension immédiate du compte et l'engagement de poursuites.
        </p>
      </>
    ),
  },
  {
    title: "6. Escrow, commission et facturation",
    body: (
      <>
        <p>
          Les paiements acheteur sont placés sous séquestre via Stripe Connect jusqu'à confirmation du
          rendez-vous. La commission de la plateforme est fixée à <strong>10% HT</strong> du prix de vente,
          prélevée au vendeur. L'acheteur n'acquitte aucune commission.
        </p>
        <p>
          L'émetteur unique de toutes les factures est <strong>KLIKANGO SAS (marque RdvMarket)</strong>.
          Les noms et coordonnées des vendeurs ne figurent jamais sur les factures adressées aux acheteurs :
          l'objet est systématiquement présenté comme « Lead qualifié — Secteur X ».
        </p>
      </>
    ),
  },
  {
    title: "7. Données personnelles — compte utilisateur",
    body: (
      <>
        <p>
          Le responsable de traitement des données du compte utilisateur (email, nom, SIRET/BCE/IDE, pièces KYC)
          est KLIKANGO SAS. Base légale : exécution du contrat (art. 6-1-b RGPD) et obligation légale (lutte
          anti-blanchiment, LCB-FT).
        </p>
        <p>
          Lors des connexions authentifiées à la plateforme, la <strong>dernière adresse IP</strong> observée
          (telle que transmise par l’hébergeur ou le réseau de diffusion, ex. en-tête « X-Forwarded-For ») peut
          être enregistrée et mise à jour de façon espacée à des fins de <strong>sécurité et lutte contre la
          fraude</strong> (base légale : intérêt légitime, art. 6-1-f RGPD).
        </p>
        <p>
          Durée de conservation : 5 ans après clôture du compte pour les données comptables et KYC, 3 ans pour
          les données de prospection commerciale.
        </p>
        <p>
          L'utilisateur peut exercer ses droits (accès, rectification, effacement, portabilité, opposition) en
          écrivant à <strong>rgpd@rdvmarket.fr</strong> ou à l'adresse postale du siège. Une réclamation peut
          être déposée auprès de la CNIL (<em>www.cnil.fr</em>).
        </p>
      </>
    ),
  },
  {
    title: "8. Acceptation et preuve",
    body: (
      <>
        <p>
          En cochant la case « J'ai lu et j'accepte la présente charte » lors de l'inscription, l'utilisateur
          reconnaît avoir pris connaissance de l'intégralité du présent document, l'accepte sans réserve, et
          confirme avoir la capacité juridique pour engager son entreprise.
        </p>
        <p>
          L'acceptation est horodatée côté serveur et conservée à titre de preuve. La version en vigueur est
          numérotée ({CHARTER_VERSION}) ; toute modification substantielle fera l'objet d'une nouvelle acceptation
          lors de la prochaine connexion.
        </p>
      </>
    ),
  },
];

const Charte = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Charte et CGU"
        description="Charte d'utilisation, conformité RGPD et conditions générales RdvMarket (KLIKANGO SAS)."
        canonicalPath="/charte"
      />
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-x-6 mb-6">
          <button
            type="button"
            className="inline-flex w-fit items-center gap-1 text-sm text-slate-500 hover:text-[#2D7FF9]"
            onClick={() => goBackOr(navigate)}
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <div className="inline-flex items-center gap-2 bg-[#2D7FF9]/10 text-[#2D7FF9] px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase shrink-0 self-end sm:self-auto">
            <ShieldCheck className="w-3.5 h-3.5" />Charte RGPD & CGU · Version {CHARTER_VERSION}
          </div>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-4">
          Charte d'utilisation et de conformité.
        </h1>
        <p className="text-slate-500 mb-10">
          Dernière mise à jour : 21 avril 2026. Ce document constitue la charte d'engagement de tout utilisateur
          de RdvMarket (exploité par KLIKANGO SAS).
        </p>

        <div className="border border-[#E5E7EB] rounded-lg p-5 mb-10 bg-slate-50 flex items-start gap-3 text-sm">
          <Building2 className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div className="text-slate-700">
            <strong>Éditeur :</strong> KLIKANGO — SAS au capital de 2 008 € · SIREN 948 465 331 ·
            SIRET 948 465 331 00015 · RCS Créteil · TVA FR42 948 465 331 · 16 Avenue Sainte-Marie, 94160 Saint-Mandé, France.
          </div>
        </div>

        <div className="prose prose-slate max-w-none space-y-10" data-testid="charter-content">
          {CHARTER_SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="font-display font-bold text-xl tracking-tight text-[#0B0D12] mb-3">{s.title}</h2>
              <div className="text-sm text-slate-700 leading-relaxed space-y-2">{s.body}</div>
            </section>
          ))}
        </div>

        <div className="mt-14 border-t border-[#E5E7EB] pt-6 flex items-center gap-3 text-xs text-slate-400">
          <Lock className="w-4 h-4" />
          L'acceptation horodatée de cette charte est exigée à chaque inscription.
          <FileText className="w-4 h-4 ml-auto" />
          Version {CHARTER_VERSION}
        </div>
      </div>
    </div>
  );
};

export default Charte;
